import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../utils/apiClient';
import { useParams, Link } from 'react-router-dom';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { hasEffectivePermission } from '../utils/accessControl';
import AppIcon from '../components/AppIcon';
import '../styles/ProjectDetail.css';

const API_BASE = API_BASE_URL;

const STATUS_LABELS = {
  planning: 'Planlama',
  in_progress: 'Devam Ediyor',
  completed: 'Tamamlandı',
  on_hold: 'Askıda',
};

const STATUS_COLORS = {
  planning: '#f59f28',
  in_progress: '#40a853',
  completed: '#2f78c9',
  on_hold: '#d15252',
};

const SCHEDULE_HEALTH_LABELS = {
  on_track: 'Yolunda',
  watch: 'İzleme Gerekiyor',
  at_risk: 'Riskte',
  critical: 'Kritik',
};

const ProjectDetail = () => {
  const { user, permissions } = useAuth();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [scheduleHealth, setScheduleHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canManageProjects = hasEffectivePermission(user, permissions, 'can_manage_projects');
  const canManageTasks = hasEffectivePermission(user, permissions, 'can_manage_tasks');

  const fetchProjectData = useCallback(async () => {
    try {
      setLoading(true);
      const [projectRes, tasksRes, scheduleRes] = await Promise.all([
        apiClient.get(`${API_BASE}/projects/${id}`),
        apiClient.get(`${API_BASE}/tasks/project/${id}`, {
          validateStatus: () => true,
        }),
        apiClient.get(`${API_BASE}/projects/${id}/schedule-health`, {
          validateStatus: () => true,
        }),
      ]);

      setProject(projectRes.data || null);
      setTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []);
      setScheduleHealth(scheduleRes.status === 200 ? (scheduleRes.data || null) : null);
      setError(null);
    } catch (requestError) {
      setError('Proje bilgileri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  const getProgressPercentage = () => {
    if (!project) return 0;
    if (project.status === 'completed') return 100;
    if (project.status === 'in_progress') return 60;
    if (project.status === 'planning') return 20;
    return 0;
  };

  if (loading) {
    return (
      <div className="project-detail-container">
        <div className="project-detail-state">Yükleniyor...</div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="project-detail-container">
        <div className="project-detail-state error">
          <p>{error || 'Proje bulunamadı.'}</p>
          <Link to="/projects" className="project-detail-back-link">
            Projelere Dön
          </Link>
        </div>
      </div>
    );
  }

  const statusLabel = STATUS_LABELS[project.status] || project.status;
  const statusColor = STATUS_COLORS[project.status] || '#8b9fb8';
  const healthStatus = scheduleHealth?.variance?.health_status || 'on_track';
  const healthLabel = SCHEDULE_HEALTH_LABELS[healthStatus] || healthStatus;

  return (
    <div className="project-detail-container">
      <div className="project-detail-header">
        <Link to="/projects" className="project-detail-back-link">
          <AppIcon name="chevronLeft" className="project-detail-inline-icon" />
          Projelere Dön
        </Link>
        {canManageProjects && (
          <Link to={`/projects/${id}/edit`} className="project-detail-edit-link">
            Düzenle
          </Link>
        )}
      </div>

      <div className="project-detail-grid">
        <section className="project-detail-main-card">
          <div className="project-title-row">
            <div>
              <h1>{project.name}</h1>
              <p className="project-subtitle">{project.location || 'Konum belirtilmemiş'}</p>
            </div>
            <span className="project-status-pill" style={{ backgroundColor: statusColor }}>
              {statusLabel}
            </span>
          </div>

          <div className="project-progress-block">
            <div className="project-progress-head">
              <span>Proje İlerleme</span>
              <strong>{getProgressPercentage()}%</strong>
            </div>
            <div className="project-progress-bar">
              <div className="project-progress-fill" style={{ width: `${getProgressPercentage()}%` }} />
            </div>
          </div>

          {scheduleHealth && (
            <div className="project-schedule-health-box">
              <div className="project-schedule-health-head">
                <h3>Termin Sağlığı</h3>
                <span className={`project-schedule-pill ${healthStatus}`}>{healthLabel}</span>
              </div>
              <div className="project-schedule-health-grid">
                <article>
                  <span>Baz İlerleme</span>
                  <strong>
                    {scheduleHealth.baseline?.baseline_progress_pct !== null
                      ? `%${scheduleHealth.baseline?.baseline_progress_pct}`
                      : '-'}
                  </strong>
                </article>
                <article>
                  <span>Gerçekleşen İlerleme</span>
                  <strong>%{scheduleHealth.actual?.actual_progress_pct || 0}</strong>
                </article>
                <article>
                  <span>Termin Sapması</span>
                  <strong>
                    {scheduleHealth.variance?.schedule_variance_pct !== null
                      ? `%${scheduleHealth.variance?.schedule_variance_pct}`
                      : '-'}
                  </strong>
                </article>
                <article>
                  <span>Tahmini Gecikme</span>
                  <strong>
                    {scheduleHealth.variance?.forecast_delay_days !== null
                      ? `${scheduleHealth.variance?.forecast_delay_days} gün`
                      : '-'}
                  </strong>
                </article>
              </div>
              <div className="project-schedule-reasons">
                <h4>Kayma Nedenleri</h4>
                <ul>
                  {(scheduleHealth.critical_path?.delay_reasons || []).slice(0, 3).map((reason, idx) => (
                    <li key={`delay-reason-${idx}`}>{reason}</li>
                  ))}
                </ul>
              </div>
              <div className="project-schedule-actions">
                <h4>Önerilen Aksiyonlar</h4>
                <ul>
                  {(scheduleHealth.critical_path?.recommended_actions || []).slice(0, 3).map((action, idx) => (
                    <li key={`delay-action-${idx}`}>{action}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="project-meta-grid">
            <article className="project-meta-item">
              <span>Başlangıç Tarihi</span>
              <strong>
                {project.start_date
                  ? new Date(project.start_date).toLocaleDateString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : '-'}
              </strong>
            </article>
            <article className="project-meta-item">
              <span>Bitiş Tarihi</span>
              <strong>
                {project.end_date
                  ? new Date(project.end_date).toLocaleDateString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : '-'}
              </strong>
            </article>
            <article className="project-meta-item">
              <span>Bütçe</span>
              <strong>{project.budget ? `₺${parseFloat(project.budget).toLocaleString('tr-TR')}` : '-'}</strong>
            </article>
            <article className="project-meta-item">
              <span>Toplam Görev</span>
              <strong>{tasks.length}</strong>
            </article>
          </div>

          {project.client_info && (
            <div className="project-note-box">
              <h3>Müşteri Bilgileri</h3>
              <p>{project.client_info}</p>
            </div>
          )}

          <div className="project-tasks-box">
            <div className="project-tasks-head">
              <h3>Görevler</h3>
              <Link to={`/projects/${id}/tasks`} className="project-link-action">
                Tümünü Görüntüle
              </Link>
            </div>

            {tasks.length === 0 ? (
              <div className="project-empty-box">
                <p>Henüz görev eklenmemiş.</p>
                {canManageTasks && (
                  <Link to={`/projects/${id}/tasks`} className="project-primary-action">
                    Görev Ekle
                  </Link>
                )}
              </div>
            ) : (
              <div className="project-task-list">
                {tasks.slice(0, 6).map((task) => (
                  <article key={task.id} className="project-task-item">
                    <div>
                      <h4>{task.title}</h4>
                      <p>{task.description || '-'}</p>
                    </div>
                    <div className="project-task-tags">
                      <span className={`project-tag status-${task.status}`}>
                        {STATUS_LABELS[task.status] || task.status}
                      </span>
                      <span className={`project-tag priority-${task.priority || 'medium'}`}>
                        {task.priority || 'medium'}
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        <aside className="project-detail-side">
          <section className="project-side-card">
            <h3>Hızlı Bilgiler</h3>
            <div className="project-side-item">
              <span>Durum</span>
              <strong>{statusLabel}</strong>
            </div>
            <div className="project-side-item">
              <span>Oluşturulma Tarihi</span>
              <strong>{project.created_at ? new Date(project.created_at).toLocaleDateString('tr-TR') : '-'}</strong>
            </div>
            <div className="project-side-item">
              <span>Son Güncelleme</span>
              <strong>{project.updated_at ? new Date(project.updated_at).toLocaleDateString('tr-TR') : '-'}</strong>
            </div>
            <div className="project-side-actions">
              <Link to={`/projects/${id}/tasks`} className="project-side-action-btn">
                {canManageTasks ? 'Görevleri Yönet' : 'Görevleri Gör'}
              </Link>
              <Link to="/photos" className="project-side-action-btn">
                Fotoğraflar
              </Link>
              <Link to="/daily-reports" className="project-side-action-btn">
                Raporlar
              </Link>
            </div>
          </section>

          <section className="project-side-card">
            <h3>Görev İstatistikleri</h3>
            <div className="project-stat-line">
              <span>Tamamlanan</span>
              <strong>{tasks.filter((t) => t.status === 'completed').length}</strong>
            </div>
            <div className="project-stat-line">
              <span>Devam Eden</span>
              <strong>{tasks.filter((t) => t.status === 'in_progress').length}</strong>
            </div>
            <div className="project-stat-line">
              <span>Bekleyen</span>
              <strong>{tasks.filter((t) => t.status === 'to_do').length}</strong>
            </div>
          </section>

          {scheduleHealth && (
            <section className="project-side-card">
              <h3>Kritik Yol Uyarıları</h3>
              {(scheduleHealth.critical_path?.top_blockers || []).length === 0 ? (
                <div className="project-empty-box">
                  <p>Kilitlenmiş bağımlı görev tespit edilmedi.</p>
                </div>
              ) : (
                <div className="project-side-warning-list">
                  {(scheduleHealth.critical_path?.top_blockers || []).slice(0, 3).map((blocker) => (
                    <article key={`blocker-${blocker.parent_task_id}`} className="project-side-warning-item">
                      <strong>{blocker.parent_task_title}</strong>
                      <span>Bağımlı açık iş: {blocker.dependent_open_count}</span>
                      <span>Yüksek risk bağımlılık: {blocker.high_risk_dependents}</span>
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
};

export default ProjectDetail;
